import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { useI18n } from "../i18n";
import apiClient from "../lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, Lock } from "lucide-react";

const PAYMENT_METHODS = [
  { id: "ideal", label: "iDEAL", note: "Direct via je bank", primary: true },
  { id: "klarna", label: "Klarna — Pay later", note: "Achteraf betalen" },
  { id: "card", label: "Credit / Debit card", note: "Visa, Mastercard (Stripe)" },
  { id: "paypal", label: "PayPal", note: "Pay with PayPal" },
  { id: "applepay", label: "Apple Pay", note: "Touch / Face ID" },
  { id: "googlepay", label: "Google Pay", note: "Quick checkout" },
];

const SHIPPING_OPTIONS_UI = [
  { id: "postnl_standard", label_en: "PostNL Standard (2-3 days)", label_nl: "PostNL Standaard (2-3 dagen)", price: 4.95, free_above: 50 },
  { id: "postnl_express", label_en: "PostNL Express (Next day)", label_nl: "PostNL Express (Volgende dag)", price: 9.95, free_above: null },
  { id: "dhl_standard", label_en: "DHL Netherlands (2-4 days)", label_nl: "DHL Nederland (2-4 dagen)", price: 5.95, free_above: 75 },
];

const Checkout = () => {
  const { lines, subtotal: cartSubtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const mollieReady = settings?.mollie_configured === true;
  const idealNote = mollieReady
    ? (lang === "nl" ? "Live iDEAL via Mollie." : "Live iDEAL via Mollie.")
    : (lang === "nl" ? "iDEAL nog niet beschikbaar — admin moet Mollie API key instellen." : "iDEAL not available yet — admin must set the Mollie API key.");
  const [shipping, setShipping] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    street: "",
    house_number: "",
    postal_code: "",
    city: "",
    country: "Netherlands",
  });
  const [shippingMethod, setShippingMethod] = useState("postnl_standard");
  const [paymentMethod, setPaymentMethod] = useState("ideal");
  const [discountCode, setDiscountCode] = useState("");  const [quote, setQuote] = useState(null);
  const [discountFeedback, setDiscountFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // refresh quote when key inputs change
  useEffect(() => {
    if (lines.length === 0) return;
    const body = {
      lines: lines.map((l) => ({ product_id: l.product_id, qty: l.qty })),
      shipping_method: shippingMethod,
      payment_method: paymentMethod,
      discount_code: discountCode || null,
    };
    apiClient.post("/checkout/quote", body).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [lines, shippingMethod, paymentMethod, discountCode]);

  if (lines.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-5xl tracking-tighter font-light">{t("checkout")}</h1>
        <p className="mt-4 text-ink-muted">{t("cart_empty")}</p>
        <Link to="/shop" className="btn-primary mt-8 text-sm">{t("continue_shopping")}</Link>
      </div>
    );
  }

  const applyDiscount = async () => {
    if (!discountCode) return;
    try {
      const r = await apiClient.post("/discounts/validate", { code: discountCode, subtotal: cartSubtotal });
      setDiscountFeedback(`✓ ${r.data.description} — €${r.data.discount_amount.toFixed(2)}`);
    } catch (e) {
      setDiscountFeedback("✗ " + (e?.response?.data?.detail || "Invalid code"));
    }
  };

  const placeOrder = async () => {
    if (!shipping.full_name || !shipping.email || !shipping.street || !shipping.postal_code || !shipping.city) {
      toast.error(lang === "nl" ? "Vul alle adresvelden in" : "Please fill in all address fields");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        lines: lines.map((l) => ({ product_id: l.product_id, qty: l.qty })),
        shipping,
        shipping_method: shippingMethod,
        payment_method: paymentMethod,
        discount_code: discountCode || null,
        origin_url: window.location.origin,
      };
      const { data } = await apiClient.post("/checkout", body);
      if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
      } else {
        clear();
        navigate(`/order-success?order_id=${data.order_id}&tracking=${data.tracking_code}&mocked=${data.mocked ? 1 : 0}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 lg:px-12 py-10" data-testid="checkout-page">
      <h1 className="font-display text-5xl tracking-tighter font-light mb-2">{t("one_page_checkout")}</h1>
      <p className="text-ink-muted mb-10 text-sm flex items-center gap-2"><Lock className="w-4 h-4" /> {t("pay_secure")}</p>

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 space-y-10">
          <section data-testid="checkout-contact">
            <div className="font-label text-ink-muted mb-3">01 · {t("contact")}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <input data-testid="checkout-full-name" value={shipping.full_name} onChange={(e) => setShipping({ ...shipping, full_name: e.target.value })} placeholder={t("name")} className="border border-black/15 px-4 py-3 text-sm bg-white" />
              <input data-testid="checkout-email" value={shipping.email} onChange={(e) => setShipping({ ...shipping, email: e.target.value })} placeholder={t("email")} type="email" className="border border-black/15 px-4 py-3 text-sm bg-white" />
              <input data-testid="checkout-phone" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} placeholder={lang === "nl" ? "Telefoon (optioneel)" : "Phone (optional)"} className="border border-black/15 px-4 py-3 text-sm bg-white md:col-span-2" />
            </div>
          </section>

          <section data-testid="checkout-address">
            <div className="font-label text-ink-muted mb-3">02 · {t("address")}</div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="checkout-street" value={shipping.street} onChange={(e) => setShipping({ ...shipping, street: e.target.value })} placeholder={lang === "nl" ? "Straat" : "Street"} className="col-span-2 border border-black/15 px-4 py-3 text-sm bg-white" />
              <input data-testid="checkout-house" value={shipping.house_number} onChange={(e) => setShipping({ ...shipping, house_number: e.target.value })} placeholder={lang === "nl" ? "Huisnr." : "House nr."} className="border border-black/15 px-4 py-3 text-sm bg-white" />
              <input data-testid="checkout-postal" value={shipping.postal_code} onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })} placeholder={lang === "nl" ? "Postcode (1234 AB)" : "Postal code"} className="border border-black/15 px-4 py-3 text-sm bg-white" />
              <input data-testid="checkout-city" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} placeholder={lang === "nl" ? "Plaats" : "City"} className="col-span-2 border border-black/15 px-4 py-3 text-sm bg-white" />
              <select value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} className="col-span-3 border border-black/15 px-4 py-3 text-sm bg-white">
                <option>Netherlands</option>
                <option>Belgium</option>
                <option>Germany</option>
              </select>
            </div>
          </section>

          <section data-testid="checkout-shipping-method">
            <div className="font-label text-ink-muted mb-3">03 · {t("shipping")}</div>
            <div className="space-y-2">
              {SHIPPING_OPTIONS_UI.map((s) => {
                const free = s.free_above && cartSubtotal >= s.free_above;
                return (
                  <label key={s.id} data-testid={`shipping-${s.id}`} className={`flex items-center justify-between border ${shippingMethod === s.id ? "border-ink" : "border-black/10"} px-4 py-4 cursor-pointer bg-white`}>
                    <div className="flex items-center gap-3">
                      <input type="radio" name="ship" checked={shippingMethod === s.id} onChange={() => setShippingMethod(s.id)} />
                      <div>
                        <div className="text-sm font-medium">{lang === "nl" ? s.label_nl : s.label_en}</div>
                        {s.free_above && <div className="text-xs text-ink-muted">{lang === "nl" ? `Gratis vanaf €${s.free_above}` : `Free above €${s.free_above}`}</div>}
                      </div>
                    </div>
                    <div className="text-sm font-display">{free ? <span className="text-olive">FREE</span> : `€${s.price.toFixed(2)}`}</div>
                  </label>
                );
              })}
            </div>
          </section>

          <section data-testid="checkout-payment">
            <div className="font-label text-ink-muted mb-3">04 · {t("payment")}</div>
            <div className="grid md:grid-cols-2 gap-2">
              {PAYMENT_METHODS.map((p) => (
                <label key={p.id} data-testid={`payment-${p.id}`} className={`relative flex items-center gap-3 border ${paymentMethod === p.id ? "border-ink bg-secondary-warm" : "border-black/10"} ${p.primary ? "ring-1 ring-terracotta/40" : ""} px-4 py-4 cursor-pointer bg-white`}>
                  {p.primary && <span className="absolute -top-2 left-3 bg-terracotta text-white text-[10px] font-label px-2 py-0.5">{lang === "nl" ? "AANBEVOLEN" : "RECOMMENDED"}</span>}
                  <input type="radio" name="pay" checked={paymentMethod === p.id} onChange={() => setPaymentMethod(p.id)} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-xs text-ink-muted">{p.note}</div>
                  </div>
                </label>
              ))}
            </div>
            {paymentMethod === "ideal" && (
              <div className={`mt-3 text-xs ${mollieReady ? "text-olive" : "text-terracotta"}`} data-testid="ideal-status-note">
                {idealNote}
              </div>
            )}
            {paymentMethod !== "card" && paymentMethod !== "ideal" && (
              <div className="mt-3 text-xs text-terracotta">
                {lang === "nl" ? "Demo: betaling wordt gesimuleerd — geen echte transactie." : "Demo: this payment is MOCKED — no real transaction will occur."}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3 pt-4 border-t border-black/5">
              <span className="font-label text-ink-muted">{lang === "nl" ? "Veilig betalen via" : "Secure payment via"}</span>
              {["iDEAL", "Mollie", "Stripe", "Klarna", "PayPal", "Visa", "Mastercard", "Apple Pay", "Google Pay"].map((p) => (
                <span key={p} className="border border-black/15 px-2.5 py-1 text-[10px] font-label tracking-wider bg-white">{p}</span>
              ))}
            </div>
          </section>
        </div>

        <aside className="md:col-span-5">
          <div className="bg-white border border-black/10 p-6 sticky top-32">
            <div className="font-display text-2xl mb-5">Order summary</div>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {lines.filter((l) => l && l.snapshot).map((l) => (
                <div key={l.product_id} className="flex gap-3 items-start">
                  <div className="w-14 h-16 bg-secondary-warm overflow-hidden shrink-0"><img src={l.snapshot?.image} alt="" className="w-full h-full object-cover" /></div>
                  <div className="flex-1 text-sm">
                    <div>{lang === "nl" ? l.snapshot?.name_nl : l.snapshot?.name_en}</div>
                    <div className="text-ink-muted text-xs">{l.qty} × €{Number(l.snapshot?.price || 0).toFixed(2)}</div>
                  </div>
                  <div className="text-sm font-display">€{(l.qty * Number(l.snapshot?.price || 0)).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-black/10 pt-4">
              <div className="flex gap-2">
                <input data-testid="discount-input" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} placeholder={t("discount_code")} className="border border-black/15 px-3 py-2 text-sm flex-1" />
                <button data-testid="discount-apply" onClick={applyDiscount} className="btn-secondary text-xs px-4">{t("apply")}</button>
              </div>
              {discountFeedback && <div className="text-xs mt-2 text-ink-muted" data-testid="discount-feedback">{discountFeedback}</div>}
            </div>

            <div className="mt-5 text-sm space-y-2">
              <div className="flex justify-between"><span>{t("subtotal")}</span><span data-testid="summary-subtotal">€{(quote?.subtotal ?? cartSubtotal).toFixed(2)}</span></div>
              <div className="flex justify-between"><span>{t("shipping")}</span><span>€{(quote?.shipping_cost ?? 0).toFixed(2)}</span></div>
              {quote?.discount_amount > 0 && <div className="flex justify-between text-olive"><span>{t("discount")} ({quote.discount_code})</span><span>−€{quote.discount_amount.toFixed(2)}</span></div>}
              {quote?.payment_fee > 0 && <div className="flex justify-between"><span>{t("payment_fee")}</span><span>€{quote.payment_fee.toFixed(2)}</span></div>}
              {(quote?.vat_breakdown || []).map((b) => (
                <div key={b.rate} className="flex justify-between text-xs text-ink-muted" data-testid={`summary-vat-${b.rate}`}>
                  <span>{lang === "nl" ? "BTW" : "VAT"} {b.rate}%</span>
                  <span>€{b.vat.toFixed(2)}</span>
                </div>
              ))}
              {quote?.total_net != null && (
                <div className="flex justify-between text-xs text-ink-muted" data-testid="summary-net">
                  <span>{lang === "nl" ? "Excl. BTW" : "Ex. VAT"}</span>
                  <span>€{quote.total_net.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-black/10 pt-3 flex justify-between font-display text-lg"><span>{t("total")}</span><span data-testid="summary-total">€{(quote?.grand_total ?? cartSubtotal).toFixed(2)}</span></div>
              <div className="text-xs text-ink-muted">{t("vat_incl")}</div>
            </div>

            <button data-testid="place-order-btn" onClick={placeOrder} disabled={submitting} className="btn-primary w-full mt-5 text-sm">
              {submitting ? "…" : <>{t("place_order")} <ArrowRight className="w-4 h-4" /></>}
            </button>
            <div className="text-[10px] mt-3 text-ink-muted flex items-center gap-1.5"><Check className="w-3 h-3" /> {t("by_signing_up")}</div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
